import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Send } from "lucide-react"

import { Button } from "@/components/button"
import { DateField } from "@/components/custom/date-field"
import { StatusAlert } from "@/components/shared/status-alert"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { absenceTrackingApi } from "@/lib/absence-tracking-api"
import {
  type AbsencesUiStore,
  useAbsencesEndDate,
  useAbsencesMessage,
  useAbsencesReason,
  useAbsencesStartDate,
} from "@/lib/stores/absences-ui-store"

const absenceDashboardQueryKey = ["absence-tracking", "me"] as const

const toUtcRange = (startDate: string, endDate: string) => ({
  startAt: `${startDate}T00:00:00.000Z`,
  endAt: `${endDate}T23:59:59.999Z`,
})

export function AbsenceApplyCard({
  store,
  reviewRequired,
}: {
  store: AbsencesUiStore
  reviewRequired: boolean
}) {
  const queryClient = useQueryClient()
  const startDate = useAbsencesStartDate(store)
  const endDate = useAbsencesEndDate(store)
  const reason = useAbsencesReason(store)
  const message = useAbsencesMessage(store)

  const applyMutation = useMutation({
    mutationFn: absenceTrackingApi.apply,
    onSuccess: async () => {
      store.getState().setMessage({
        tone: "success",
        text: reviewRequired
          ? "Absence submitted — admins have been notified"
          : "Absence submitted",
      })
      store.getState().resetForm()
      await queryClient.invalidateQueries({
        queryKey: absenceDashboardQueryKey,
      })
    },
    onError: (error) => {
      store.getState().setMessage({
        tone: "error",
        text:
          error instanceof Error ? error.message : "Failed to submit absence",
      })
    },
  })

  const canSubmit =
    startDate.length > 0 &&
    endDate.length > 0 &&
    !applyMutation.isPending &&
    startDate <= endDate
  return (
    <Card>
      <CardHeader>
        <CardTitle>Notify about an absence</CardTitle>
        <CardDescription>
          {reviewRequired
            ? "Admins will be notified when you submit an absence."
            : "New absences become active as soon as you submit them."}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="grid gap-2 text-sm font-medium">
            Start date
            <DateField
              value={startDate}
              onValueChange={(value) =>
                store.getState().setStartDate(value ?? "")
              }
            />
          </label>
          <label className="grid gap-2 text-sm font-medium">
            End date
            <DateField
              minValue={startDate}
              value={endDate}
              onValueChange={(value) =>
                store.getState().setEndDate(value ?? "")
              }
            />
          </label>
        </div>
        <label className="grid gap-2 text-sm font-medium">
          Reason
          <Textarea
            value={reason}
            onChange={(event) => store.getState().setReason(event.target.value)}
            placeholder="Optional context"
            rows={4}
          />
        </label>
        {startDate.length > 0 && endDate.length > 0 && startDate > endDate ? (
          <div className="text-sm text-destructive" role="alert">
            End date must be on or after the start date.
          </div>
        ) : null}
        {message ? (
          <StatusAlert
            tone={message.tone === "success" ? "success" : "danger"}
            intensity={message.tone === "error" ? "subtle" : undefined}
          >
            {message.text}
          </StatusAlert>
        ) : null}
        <div className="flex justify-end">
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              const range = toUtcRange(startDate, endDate)
              void applyMutation.mutateAsync({
                ...range,
                reason: reason.trim() || null,
              })
            }}
          >
            <Send className="size-4" />
            {applyMutation.isPending ? "Submitting..." : "Submit absence"}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
